import React, { useEffect, useState } from 'react';
import { FiUsers, FiMaximize2, FiShoppingCart, FiDollarSign, FiStar, FiRefreshCw, FiArrowUpRight, FiCheckCircle, FiClock, FiTruck } from 'react-icons/fi';
import { getDashboard } from '../services/api';

const fmtNum = (n: number) => {
  if (n >= 100000) return `${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return n.toLocaleString('en-IN');
  return String(n);
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await getDashboard();
      setData(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!data) return <div className="empty">Failed to load dashboard</div>;

  const u = data.users || {};
  const qr = data.qrCodes || {};
  const ord = data.orders || {};

  return (
    <div>
      {/* Hero Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {/* Total Active Users */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 22px', border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #43A047, #66BB6A)' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiUsers style={{ fontSize: 20, color: '#16a34a' }} />
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#f0fdf4', padding: '3px 8px', borderRadius: 6 }}>
              <FiArrowUpRight style={{ fontSize: 12 }} /> Active
            </span>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, color: '#1a1d21', lineHeight: 1 }}>{fmtNum(u.total || 0)}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6, fontWeight: 500 }}>Total Registered Users</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#43A047' }}>{u.labourers || 0}</strong> Labourers</span>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#1565C0' }}>{u.dealers || 0}</strong> Dealers</span>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#F57C00' }}>{u.contractors || 0}</strong> Contractors</span>
          </div>
        </div>

        {/* QR Redemptions */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 22px', border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #1565C0, #42A5F5)' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiMaximize2 style={{ fontSize: 20, color: '#2563eb' }} />
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '3px 8px', borderRadius: 6 }}>
              <FiCheckCircle style={{ fontSize: 12 }} /> QR Scans
            </span>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, color: '#1a1d21', lineHeight: 1 }}>{fmtNum(qr.total || 0)}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6, fontWeight: 500 }}>Total QR Codes Generated</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#22c55e' }}>{qr.redeemed || 0}</strong> Scanned</span>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#3b82f6' }}>{qr.available || 0}</strong> Available</span>
          </div>
        </div>

        {/* Labourer Payouts */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 22px', border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #F57C00, #FFB74D)' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #fef3c7, #fde68a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiDollarSign style={{ fontSize: 20, color: '#d97706' }} />
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#d97706', background: '#fffbeb', padding: '3px 8px', borderRadius: 6 }}>
              <FiDollarSign style={{ fontSize: 12 }} /> Payouts
            </span>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, color: '#1a1d21', lineHeight: 1 }}>₹{fmtNum(data.totalPayouts || 0)}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6, fontWeight: 500 }}>Labourer Instant Payouts</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#3b82f6' }}>{fmtNum(data.totalPointsIssued || 0)}</strong> Points Issued</span>
          </div>
        </div>

        {/* Redemption Orders */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 22px', border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #E53935, #EF5350)' }} />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #fee2e2, #fecaca)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FiShoppingCart style={{ fontSize: 20, color: '#dc2626' }} />
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '3px 8px', borderRadius: 6 }}>
              <FiShoppingCart style={{ fontSize: 12 }} /> Orders
            </span>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, color: '#1a1d21', lineHeight: 1 }}>{fmtNum(ord.total || 0)}</div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6, fontWeight: 500 }}>Redemption Orders</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#f59e0b' }}>{ord.pending || 0}</strong> Pending</span>
            <span style={{ fontSize: 11, color: '#6b7280' }}><strong style={{ color: '#22c55e' }}>{ord.delivered || 0}</strong> Delivered</span>
          </div>
        </div>
      </div>

      {/* Active Schemes + Order Pipeline */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Schemes + Points Summary */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiStar style={{ color: '#f59e0b' }} /> Program Overview</h3>
            <button className="btn btn-ghost btn-sm" onClick={loadDashboard} title="Refresh"><FiRefreshCw style={{ fontSize: 14 }} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ padding: 18, background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#16a34a', letterSpacing: -0.5 }}>{data.activeSchemes || 0}</div>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginTop: 2 }}>Active Schemes</div>
            </div>
            <div style={{ padding: 18, background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#1d4ed8', letterSpacing: -0.5 }}>{fmtNum(data.totalPointsIssued || 0)}</div>
              <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600, marginTop: 2 }}>Total Points Issued</div>
            </div>
            <div style={{ padding: 18, background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#b45309', letterSpacing: -0.5 }}>₹{fmtNum(data.totalPayouts || 0)}</div>
              <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginTop: 2 }}>Cash Payouts</div>
            </div>
            <div style={{ padding: 18, background: 'linear-gradient(135deg, #fdf2f8, #fce7f3)', borderRadius: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#be185d', letterSpacing: -0.5 }}>{u.total || 0}</div>
              <div style={{ fontSize: 12, color: '#9d174d', fontWeight: 600, marginTop: 2 }}>Registered Users</div>
            </div>
          </div>
        </div>

        {/* Order Pipeline */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiTruck style={{ color: '#6366f1' }} /> Order Pipeline</h3>
            <span className="badge badge-info">{ord.total || 0} total</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Pending', count: ord.pending || 0, icon: <FiClock />, color: '#f59e0b', bg: '#fffbeb' },
              { label: 'Approved / Processing', count: ord.approved || 0, icon: <FiCheckCircle />, color: '#3b82f6', bg: '#eff6ff' },
              { label: 'Dispatched', count: ord.dispatched || 0, icon: <FiTruck />, color: '#8b5cf6', bg: '#f5f3ff' },
              { label: 'Delivered', count: ord.delivered || 0, icon: <FiCheckCircle />, color: '#22c55e', bg: '#f0fdf4' },
            ].map((item) => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: item.bg, borderRadius: 10 }}>
                <div style={{ color: item.color, fontSize: 18 }}>{item.icon}</div>
                <div style={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: '#374151' }}>{item.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Scans */}
      <div className="card">
        <div className="card-header">
          <h3>Recent Scans</h3>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Last 10 activities</span>
        </div>
        {data.recentScans?.length > 0 ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Type</th>
                  <th>Product</th>
                  <th>Reward</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {data.recentScans.map((scan: any, i: number) => (
                  <tr key={i}>
                    <td><strong>{scan.fullName}</strong></td>
                    <td><span className="badge badge-info">{scan.userType}</span></td>
                    <td>{scan.productName}</td>
                    <td style={{ fontWeight: 600 }}>{scan.rewardType === 'cash' ? `₹${scan.rewardValue}` : `${scan.rewardValue} pts`}</td>
                    <td><span className={`badge ${scan.scanStatus === 'success' ? 'badge-success' : 'badge-danger'}`}>{scan.scanStatus}</span></td>
                    <td style={{ color: '#9ca3af', fontSize: 12.5 }}>{new Date(scan.scannedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty">
            <FiMaximize2 style={{ fontSize: 40, opacity: 0.15, display: 'block', margin: '0 auto 10px' }} />
            <p>No scan activity yet. Generate QR codes and start scanning!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
